import { IMAGES } from "@/lib/site";
import { Feather, Flower2 } from "lucide-react";

const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-[#F8FAF7] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 reveal">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#D4AF37]" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] font-medium">
              About Us
            </span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#1B4332] leading-[1.05] tracking-tight">
            Experience{" "}
            <span className="italic font-light">Authentic Malnad</span> Living
          </h2>
          <div className="mt-8 space-y-5 text-[#1A1A1A]/75 text-base md:text-lg leading-relaxed">
            <p>
              Nestled between{" "}
              <span className="text-[#1B4332] font-medium">
                Belur–Sakleshpur
              </span>{" "}
              in the beautiful Western Ghats, Navilukallu Homestay offers a
              peaceful escape surrounded by lush coffee plantations, misty
              hills and breathtaking greenery.
            </p>
            <p>
              Our traditional Malnad home preserves the rich culture,
              architecture and warm hospitality of the region — making every
              guest feel truly at home. Whether you're traveling with family,
              friends, or your partner, our homestay offers a perfect blend of
              comfort, nature and unforgettable experiences.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {[
              { k: "Family", v: "Friendly" },
              { k: "Couple", v: "Perfect" },
              { k: "Nature", v: "Immersion" },
            ].map((c) => (
              <div
                key={c.k}
                className="px-4 py-3 rounded-xl border border-[#1B4332]/10 bg-white"
              >
                <div className="font-heading text-[#1B4332] text-lg">{c.k}</div>
                <div className="text-xs text-[#1A1A1A]/60">{c.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 relative reveal">
          <div className="relative">
            <div className="aspect-[4/3] md:aspect-[5/4] rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={IMAGES.heroDay}
                alt="Navilukallu Homestay day view with lush garden"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="hidden md:block absolute -bottom-10 -left-10 w-64 rounded-2xl bg-[#1B4332] text-white p-6 shadow-2xl">
              <div className="font-heading text-3xl text-[#D4AF37]">5+</div>
              <div className="text-sm text-white/80 mt-1">
                Years of hosting happy families in the Malnad tradition
              </div>
            </div>
            <div className="hidden md:block absolute -top-8 -right-6 w-40 rounded-2xl border border-[#D4AF37]/40 bg-[#F8FAF7]/80 backdrop-blur px-5 py-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">
                Since
              </div>
              <div className="font-heading text-2xl text-[#1B4332]">
                Malnad Roots
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Peacock + Flowers dual highlight */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-20 md:mt-28 grid lg:grid-cols-2 gap-6 md:gap-8">
        {/* Peacock story */}
        <div
          className="reveal relative overflow-hidden rounded-3xl shadow-2xl min-h-[440px] md:min-h-[520px]"
          data-testid="peacock-story"
        >
          <img
            src={IMAGES.peacock}
            alt="Peacock in our coffee estate"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f]/95 via-[#1B4332]/40 to-transparent" />

          <div className="relative h-full flex flex-col justify-end p-8 sm:p-10 md:p-12 text-white">
            <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-[#D4AF37]/40 text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
              <Feather size={13} strokeWidth={1.8} />
              The Peacock Story
            </div>
            <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl mt-5 leading-[1.08] tracking-tight">
              Wake Up to{" "}
              <span className="italic font-light text-[#E8C866]">
                Nature's Beauty
              </span>
            </h3>
            <p className="mt-4 text-white/85 text-sm md:text-base leading-relaxed max-w-xl">
              Every morning, beautiful peacocks visit our homestay, creating a
              peaceful and unforgettable experience for our guests. Surrounded
              by nature, you can enjoy watching these magnificent birds as
              they gracefully roam around the property. It's the perfect way
              to start your day with the sights and sounds of the countryside.
            </p>
          </div>
        </div>

        {/* Flowers highlight */}
        <div
          className="reveal relative overflow-hidden rounded-3xl shadow-2xl min-h-[440px] md:min-h-[520px]"
          data-testid="flowers-highlight"
        >
          <img
            src={IMAGES.flowersGarden}
            alt="Our flower collection at Navilukallu Homestay"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f]/95 via-[#1B4332]/40 to-transparent" />

          <div className="relative h-full flex flex-col justify-end p-8 sm:p-10 md:p-12 text-white">
            <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-[#D4AF37]/40 text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
              <Flower2 size={13} strokeWidth={1.8} />
              Our Garden
            </div>
            <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl mt-5 leading-[1.08] tracking-tight">
              A curated collection of{" "}
              <span className="italic font-light text-[#E8C866]">
                beautiful flowers
              </span>
            </h3>
            <p className="mt-3 text-white/85 text-sm md:text-base leading-relaxed max-w-md">
              Anthuriums, bougainvillea, orchids and seasonal blooms — our
              garden is one of the most photographed spots at Navilukallu.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
